'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

const GA_ID = 'G-Y2Q58WFS53'
const STORAGE_KEY = 'cookie-consent'

function disableGA() {
  ;(window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = true
}

/**
 * Server-rendered so it paints with the page instead of after hydration — waiting
 * for hydration made it the late mobile LCP element. Visitors who already chose
 * never see it: the inline consent script in app/layout.tsx marks <html data-consent>
 * before first paint and globals.css hides `.cookie-notice`; the effect below then
 * unmounts it. Same 0.28s fade/slide in and out as before (cookie-in / cookie-out).
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(true)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    let choice: string | null = null
    try {
      choice = localStorage.getItem(STORAGE_KEY)
    } catch {
      /* storage blocked — show the notice anyway */
    }
    if (choice === 'declined') disableGA()
    if (choice) setVisible(false)
  }, [])

  const decide = (choice: 'accepted' | 'declined') => {
    try {
      localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      /* ignore */
    }
    if (choice === 'declined') disableGA()
    setLeaving(true)
    setTimeout(() => setVisible(false), 280)
  }

  if (!visible) return null

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className="cookie-notice"
      style={{
        position: 'fixed',
        bottom: 'clamp(1rem, 3vw, 1.5rem)',
        left: 'clamp(1rem, 3vw, 1.5rem)',
        zIndex: 10040,
        width: 'min(22rem, calc(100vw - 2rem))',
        background: 'var(--color-abyss)',
        border: '1px solid var(--color-border)',
        borderTop: '2px solid var(--color-fire)',
        padding: '1.1rem 1.2rem',
        boxShadow: '0 24px 60px -28px rgba(0,0,0,0.9)',
        animation: `${leaving ? 'cookie-out' : 'cookie-in'} 0.28s var(--ease-out-quart) both`,
      }}
    >
      <p className="font-mono uppercase" style={{ fontSize: '0.55rem', letterSpacing: '0.24em', color: 'var(--color-fire)', marginBottom: 8 }}>
        ◇ Cookies
      </p>
      <p className="font-body" style={{ fontSize: '0.82rem', lineHeight: 1.55, color: 'var(--color-mist)', marginBottom: '0.9rem' }}>
        This site uses cookies for anonymous analytics. See the{' '}
        <Link href="/privacy" style={{ color: 'var(--color-fire)', textDecoration: 'underline' }}>
          privacy policy
        </Link>
        .
      </p>
      <div style={{ display: 'flex', gap: '0.6rem' }}>
        <button
          type="button"
          onClick={() => decide('accepted')}
          className="font-mono uppercase"
          style={{ flex: 1, fontSize: '0.62rem', letterSpacing: '0.16em', color: 'var(--color-on-fire, #050505)', background: 'var(--color-fire)', border: 'none', padding: '0.6rem 0.5rem', cursor: 'pointer' }}
        >
          Accept
        </button>
        <button
          type="button"
          onClick={() => decide('declined')}
          className="font-mono uppercase"
          style={{ flex: 1, fontSize: '0.62rem', letterSpacing: '0.16em', color: 'var(--color-stone)', background: 'transparent', border: '1px solid var(--color-border)', padding: '0.6rem 0.5rem', cursor: 'pointer' }}
        >
          Decline
        </button>
      </div>
    </div>
  )
}
